<template>
  <b-container v-if="user" class="h-100">
    <h4 class="sticky-top bg-light rounded-bottom text-black">
      {{ $t("Indexations en cours") }}
    </h4>
    <template v-if="currentIndexations">
      <b-row align-h="center" class="pb-10">
        <b-col
          v-for="{ id, acceptedIssueSuggestion, pages } of currentIndexations"
          :key="id"
          cols="12"
          class="d-flex p-5"
          md="3"
        >
          <router-link
            :to="`/indexation/${id}`"
            class="d-flex position-relative flex-grow-1 flex-column justify-content-center align-items-center bg-light p-5"
          >
            <b-button
              class="position-absolute top-0 end-0 text-danger bg-light p-0 border-0"
              @click.stop.prevent="deleteIndexation(id)"
            >
              <i-bi-x
                v-b-tooltip="{ title: $t('Supprimer') }"
                class="display-6"
            /></b-button>
            <b-img
              :blank-color="pages[0]?.image?.url ? undefined : 'lightgrey'"
              :src="pages[0]?.image?.url || undefined"
              fluid
            />
            <div class="position-absolute bottom-0 pb-3">
              <Issue
                v-if="
                  acceptedIssueSuggestion &&
                  publicationNames[acceptedIssueSuggestion?.publicationcode]
                "
                :issue="acceptedIssueSuggestion"
              />
              <Issue v-else />
            </div>
          </router-link>
        </b-col>
      </b-row>
      <h4 v-if="!currentIndexations.length" fluid>
        {{ $t("Aucune indexation en cours") }}
      </h4></template
    >
    <template v-else>{{ $t("Chargement...") }}</template></b-container
  >
  <b-container
    v-else
    class="h-100 flex-column justify-content-center align-items-center d-flex"
  >
    <h4>
      <p>{{ $t("Vous devez être connecté pour lister vos indexations.") }}</p>
      <a :href="loginUrl">{{ $t("Connexion") }}</a>
    </h4>
    <b-alert model-value class="mt-5 text-sm">
      {{
        $t(
          "Si vous possédez l'URL d'une indexation existante, vous pouvez y accéder directement.",
        )
      }}
    </b-alert>
  </b-container>
  <b-container fluid class="position-absolute bottom-0 start-0">
    <div class="p-5">
      <b-button @click="modal = !modal">{{
        $t("Nouvelle indexation")
      }}</b-button>
    </div>
    <b-modal
      id="new-indexation-modal"
      v-model="modal"
      :title="$t('Nouvelle indexation')"
      :cancel-title="$t('Annuler')"
      align="center"
      centered
      @ok.prevent="form!.$el.reportValidity() && createIndexation()"
    >
      <b-form ref="form" @submit.prevent.stop="createIndexation">
        {{
          $t(
            "Combien de pages le magazine contient-il ? (y compris les pages que vous ne souhaitez pas indexer)",
          )
        }}
        <b-form-input
          v-model.number="totalPages"
          type="number"
          min="4"
          max="996" /></b-form></b-modal
  ></b-container>
</template>

<script setup lang="ts">
import { dumiliSocketInjectionKey } from "~/composables/useDumiliSocket";
import type { EventOutput } from "socket-call-client";
import type { ClientEmitEvents as IndexationsEvents } from "~dumili-services/indexations";

const router = useRouter();
const {
  indexationCreationSocket,
  indexationsSocket,
  getIndexationSocketFromId,
} = inject(dumiliSocketInjectionKey)!;

const { fetchPublicationNames } = coa();
const { publicationNames } = storeToRefs(coa());
const { user } = storeToRefs(collection());

const form = ref<HTMLFormElement>();
const currentIndexations =
  shallowRef<EventOutput<IndexationsEvents, "getIndexations">>();
const modal = ref(false);
const totalPages = ref(16);

const loginUrl = computed(
  () => `${import.meta.env.VITE_DM_URL}/login?redirect=${document.URL}`,
);

const createIndexation = async () => {
  const uuid = await indexationCreationSocket.value.create(totalPages.value);
  router.push(`/indexation/${uuid}`);
};

const deleteIndexation = async (id: string) => {
  const indexationSocket = getIndexationSocketFromId(id);

  await indexationSocket.deleteIndexation();
  window.location.reload();
};

watch(currentIndexations, (indexations) => {
  fetchPublicationNames(
    indexations!
      .map(
        ({ acceptedIssueSuggestion }) =>
          acceptedIssueSuggestion?.publicationcode,
      )
      .filter(Boolean) as string[],
  );
});

(async () => {
  currentIndexations.value = await indexationsSocket.value.getIndexations();
})();
</script>

<style lang="scss">
.modal {
  @media (min-width: 576px) {
    .modal-dialog {
      max-width: 800px;
    }
  }
}
</style>
